import type { IndexProduct } from '@/domains/products/types/models/IndexProduct.types'
import type { IndexProductVariant } from '@/domains/variants/types/models/IndexProductVariant.types'

interface SkuMatch {
  matchedSku: string
  matchedVariant: IndexProductVariant
}

/**
 * Composable for finding which of a product's variants carry a given SKU code.
 */
export const useSkuMatcher = () => {
  /**
   * Find every variant whose SKU code equals the search query.
   *
   * A product can hold one barcode on several variants. The supplier prints a single EAN, and the
   * venue splits it into a warm and a cold variant. So this returns a list, and the caller shows
   * all of it.
   *
   * @param searchQuery - The search string entered by the user
   * @param product - The product data with variants
   * @returns One entry per matching variant, in variant order. Empty when nothing matches.
   */
  const findMatchingVariants = (searchQuery: string | undefined, product: IndexProduct): SkuMatch[] => {
    if (!searchQuery || !product.variants?.length) {
      return []
    }

    const normalizedQuery = searchQuery.trim().toLowerCase()

    return product.variants.flatMap(variant => {
      // One entry per variant, never per code. A variant that holds the same code as both an EAN
      // and a SupRef is still one thing to pick at the till.
      const matchedCode = variant.sku_codes?.find(skuCode => skuCode.code.toLowerCase() === normalizedQuery)

      if (!matchedCode) {
        return []
      }

      return [
        {
          matchedSku: matchedCode.code,
          matchedVariant: variant
        }
      ]
    })
  }

  return {
    findMatchingVariants
  }
}

export type { SkuMatch }
